import { api } from '@/api';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import type { BookedTripDTO } from '@/types/dto/agent';
import { getBookingPromoCodeStrings } from '@/utils/changeTrips';
import type { SearchFormValues } from '@turnit-ride-ui/webshop-search-widget';
import { useEffect, useMemo, useState } from 'react';

/**
 * Re-validates the promo codes that were applied to the booking the user
 * is currently modifying. Runs once per change-booking entry (keyed on the
 * code-string set) and returns the validated `{ code, name, description }`
 * tuples in the shape the search widget's form expects.
 *
 * Codes that fail validation (expired, withdrawn, region-locked, etc.) are
 * silently dropped from the result — the original booking still benefits
 * from them, but the new search won't carry them forward. Successful
 * validations land in `formValues.promoCodes` so the user can add or
 * remove codes via the standard widget UI.
 */
export const useValidatedChangeBookingPromoCodes =
  (): SearchFormValues['promoCodes'] => {
    const { hasSelection, selectedOutboundTrip, selectedInboundTrip } =
      useChangeBookingSelection();
    const codeStrings = useMemo(() => {
      if (!hasSelection) return [];
      const selectedTrips = [selectedOutboundTrip, selectedInboundTrip].filter(
        (trip): trip is BookedTripDTO => trip !== null
      );
      return getBookingPromoCodeStrings(selectedTrips);
    }, [hasSelection, selectedOutboundTrip, selectedInboundTrip]);
    // Stringified key so the effect only re-runs when the actual code set
    // changes, not on every render that produces a new array reference.
    const codeKey = useMemo(
      () => codeStrings.slice().sort().join('|'),
      [codeStrings]
    );

    const [validated, setValidated] = useState<SearchFormValues['promoCodes']>(
      []
    );

    useEffect(() => {
      if (codeStrings.length === 0) {
        setValidated([]);
        return;
      }
      let cancelled = false;

      Promise.all(
        codeStrings.map(async (code) => {
          try {
            const { data } =
              await api.agentApi.ValidatePromotionCode_ValidatePromotionCode(
                null,
                { code }
              );
            const promotion = data.promotions?.[0];
            if (!promotion?.name || !promotion?.description) return null;
            return {
              code,
              name: promotion.name,
              description: promotion.description,
            };
          } catch {
            return null;
          }
        })
      ).then((results) => {
        if (cancelled) return;
        setValidated(
          results.filter(
            (entry): entry is NonNullable<typeof entry> => entry !== null
          )
        );
      });

      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codeKey]);

    return validated;
  };
